import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Search, 
  ShoppingCart, 
  Trash2, 
  Globe,
  MapPin,
  Wifi,
  WifiOff,
  Check,
  Loader2,
  Info,
  DollarSign,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const API = `${process.env.REACT_APP_API_URL}/api`;

const COUNTRY_OPTIONS = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
];

const AREA_CODES = {
  CA: [
    { code: "416", city: "Toronto" },
    { code: "647", city: "Toronto" },
    { code: "905", city: "GTA" },
    { code: "604", city: "Vancouver" },
    { code: "778", city: "Vancouver" },
    { code: "403", city: "Calgary" },
    { code: "780", city: "Edmonton" },
    { code: "514", city: "Montreal" },
    { code: "613", city: "Ottawa" },
  ],
  US: [
    { code: "212", city: "New York" },
    { code: "310", city: "Los Angeles" },
    { code: "312", city: "Chicago" },
    { code: "415", city: "San Francisco" },
    { code: "305", city: "Miami" },
    { code: "713", city: "Houston" },
    { code: "214", city: "Dallas" },
    { code: "404", city: "Atlanta" },
    { code: "206", city: "Seattle" },
  ],
};

export default function PhoneProvisioning() {
  const { getAuthHeader, business } = useAuth();
  
  const [hybridStatus, setHybridStatus] = useState(null);
  const [myNumbers, setMyNumbers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  
  const [searchParams, setSearchParams] = useState({
    country_code: "CA",
    area_code: "",
    limit: 10
  });
  
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState("");

  useEffect(() => {
    // Check if we have business context
    if (business?.id) {
      fetchData();
    } else {
      // Still fetch status even without business
      fetchStatusOnly();
    }
  }, [business?.id]);

  const fetchStatusOnly = async () => {
    try {
      const statusRes = await axios.get(`${API}/telephony/hybrid/status`, { headers: getAuthHeader() });
      setHybridStatus(statusRes.data);
    } catch (error) {
      console.error("Failed to load status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statusRes, numbersRes, agentsRes] = await Promise.all([
        axios.get(`${API}/telephony/hybrid/status`, { headers: getAuthHeader() }),
        axios.get(`${API}/telephony/provision/my-numbers/${business?.id || 'unknown'}`, { headers: getAuthHeader() }),
        axios.get(`${API}/agents`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setHybridStatus(statusRes.data);
      setMyNumbers(numbersRes.data.numbers || []);
      // Filter agents safely
      const agentList = Array.isArray(agentsRes.data) ? agentsRes.data : [];
      setAgents(agentList.filter(a => a.is_active !== false));
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load phone provisioning data");
    } finally {
      setLoading(false);
    }
  };

  const searchNumbers = async () => {
    setSearching(true);
    setSearchResults([]);
    
    try {
      const response = await axios.post(
        `${API}/telephony/provision/search`,
        searchParams,
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
      );
      setSearchResults(response.data.numbers || []);
      
      if (response.data.mock_notice) {
        toast.info("Demo Mode: Showing simulated numbers");
      }
    } catch (error) {
      toast.error("Failed to search for phone numbers");
    } finally {
      setSearching(false);
    }
  };

  const openPurchaseDialog = (number) => {
    setSelectedNumber(number);
    setSelectedAgent("");
    setShowPurchaseDialog(true);
  };

  const purchaseNumber = async () => {
    if (!selectedNumber) return;
    
    if (!business?.id) {
      toast.error("Business information not loaded. Please try again.");
      return;
    }
    
    setPurchasing(selectedNumber.phone_number);
    
    try {
      const response = await axios.post(
        `${API}/telephony/provision/purchase`,
        {
          phone_number: selectedNumber.phone_number,
          business_id: business?.id,
          agent_id: selectedAgent || null
        },
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
      );
      
      if (response.data.success) {
        toast.success(`Phone number ${selectedNumber.formatted || selectedNumber.phone_number} purchased!`);
        setShowPurchaseDialog(false);
        setSearchResults(prev => prev.filter(n => n.phone_number !== selectedNumber.phone_number));
        fetchData();
      }
    } catch (error) {
      console.error("Purchase error:", error.response?.data);
      let errMsg = "Failed to purchase phone number";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errMsg = error.response.data;
        } else if (error.response.data.detail) {
          // Handle FastAPI validation errors
          if (Array.isArray(error.response.data.detail)) {
            errMsg = error.response.data.detail.map(e => e.msg || e).join(", ");
          } else if (typeof error.response.data.detail === 'string') {
            errMsg = error.response.data.detail;
          }
        } else if (error.response.data.msg) {
          errMsg = error.response.data.msg;
        }
      }
      toast.error(errMsg);
    } finally {
      setPurchasing(null);
    }
  };

  const releaseNumber = async (phoneNumber) => {
    if (!confirm(`Release ${phoneNumber}? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API}/telephony/provision/release/${encodeURIComponent(phoneNumber)}?business_id=${business?.id}`,
        { headers: getAuthHeader() }
      );
      toast.success("Phone number released");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to release phone number");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="phone-provisioning-page">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Phone Numbers</h1>
        <p className="text-muted-foreground">Search, purchase, and manage phone numbers for your AI agents</p>
      </div>

      {/* Service Status Card */}
      <Card className="mb-6 bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {hybridStatus?.is_live ? (
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-emerald-500" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <WifiOff className="w-6 h-6 text-amber-500" />
                </div>
              )}
              <div>
                <p className="font-semibold text-lg">
                  {hybridStatus?.is_live ? "Live Mode" : "Demo Mode"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hybridStatus?.is_live 
                    ? "Connected to Telnyx - Real phone numbers available"
                    : "Using simulated numbers for testing"}
                </p>
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={hybridStatus?.is_live 
                ? "text-emerald-400 border-emerald-400" 
                : "text-amber-400 border-amber-400"
              }
            >
              {hybridStatus?.mode?.toUpperCase() || "MOCK"}
            </Badge>
          </div>
          
          {!hybridStatus?.is_live && (
            <Alert className="mt-4 bg-amber-500/10 border-amber-500/30">
              <Info className="w-4 h-4" />
              <AlertTitle>Demo Mode Active</AlertTitle>
              <AlertDescription>
                Phone numbers shown are simulated. Contact your administrator to configure 
                Telnyx credentials for real phone numbers.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Panel */}
        <Card className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search Available Numbers
            </CardTitle>
            <CardDescription>Find phone numbers in your preferred area</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select 
                  value={searchParams.country_code} 
                  onValueChange={(v) => setSearchParams({ ...searchParams, country_code: v, area_code: "" })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Area Code</Label>
                <Select 
                  value={searchParams.area_code || "any"} 
                  onValueChange={(v) => setSearchParams({ ...searchParams, area_code: v === "any" ? "" : v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Area</SelectItem>
                    {(AREA_CODES[searchParams.country_code] || []).map(a => (
                      <SelectItem key={a.code} value={a.code}>
                        {a.code} - {a.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button 
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-500"
              onClick={searchNumbers}
              disabled={searching}
              data-testid="search-numbers-btn"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search Numbers
                </>
              )}
            </Button>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                <p className="text-sm text-muted-foreground mb-2">
                  Found {searchResults.length} available numbers
                </p>
                {searchResults.map((number) => (
                  <div 
                    key={number.phone_number}
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-cyan-500/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-cyan-400" />
                      <div>
                        <p className="font-mono font-semibold">{number.formatted || number.phone_number}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {number.region || "Local"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-emerald-400 flex items-center">
                        <DollarSign className="w-3 h-3" />
                        {number.monthly_cost}/mo
                      </span>
                      <Button 
                        size="sm" 
                        onClick={() => openPurchaseDialog(number)}
                        className="bg-emerald-600 hover:bg-emerald-500"
                        data-testid={`purchase-${number.phone_number}`}
                      >
                        <ShoppingCart className="w-3 h-3 mr-1" />
                        Get
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Numbers Panel */}
        <Card className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  My Phone Numbers
                </CardTitle>
                <CardDescription>
                  {myNumbers.length} number{myNumbers.length !== 1 ? "s" : ""} provisioned
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchData}
                className="border-slate-600"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {myNumbers.length === 0 ? (
              <div className="text-center py-8">
                <Phone className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                <h3 className="text-lg font-semibold mb-2">No Phone Numbers Yet</h3>
                <p className="text-muted-foreground text-sm">
                  Search and purchase phone numbers to start receiving calls
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {myNumbers.map((number) => (
                  <div 
                    key={number.id || number.phone_number}
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="font-mono font-semibold">{number.phone_number}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {number.mode === "mock" ? "Demo" : "Live"}
                          </Badge>
                          <span>{new Date(number.purchased_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {number.status === "active" && (
                        <Badge className="bg-emerald-500/20 text-emerald-400">
                          <Check className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        onClick={() => releaseNumber(number.phone_number)}
                        data-testid={`release-${number.phone_number}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Purchase Phone Number</DialogTitle>
            <DialogDescription>
              {hybridStatus?.is_live 
                ? "This will purchase a real phone number from Telnyx."
                : "Demo mode: This will create a simulated phone number."}
            </DialogDescription>
          </DialogHeader>
          
          {selectedNumber && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-3 mb-2">
                  <Phone className="w-5 h-5 text-cyan-400" />
                  <span className="font-mono text-lg font-semibold">
                    {selectedNumber.formatted || selectedNumber.phone_number}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selectedNumber.region || "Local"}
                  </span>
                  <span className="text-emerald-400">
                    ${selectedNumber.monthly_cost}/month
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Assign to Agent (Optional)</Label>
                <Select value={selectedAgent || "none"} onValueChange={(v) => setSelectedAgent(v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No agent</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} - {agent.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Calls to this number will be handled by the selected agent
                </p>
              </div>
              
              {!hybridStatus?.is_live && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <Info className="w-4 h-4" />
                  <AlertDescription className="text-xs">
                    This is a demo purchase. The number won't be connected to real phone networks.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={purchaseNumber}
              disabled={purchasing}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {purchasing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Purchasing...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Purchase Number
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
